<template>
  <component :is="embedded ? 'div' : 'v-container'" :fluid="!embedded" class="movement-analytics pa-4">
    <v-row>
      <v-col cols="12">
        <div class="d-flex align-center justify-space-between mb-4">
          <div>
            <h1 class="text-h5 font-weight-bold theme-heading">
              Movement Analytics
            </h1>
            <p class="text-subtitle-2 grey--text text--darken-1 mb-0">
              Talent movement tracking and engagement health metrics
            </p>
          </div>
          <div class="d-flex align-center">
            <v-btn-toggle v-model="selectedPeriodIdx" mandatory dense class="mr-3">
              <v-btn small :value="0">7d</v-btn>
              <v-btn small :value="1">14d</v-btn>
              <v-btn small :value="2">30d</v-btn>
              <v-btn small :value="3">90d</v-btn>
            </v-btn-toggle>
            <v-menu offset-y>
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon small v-bind="attrs" v-on="on">
                  <v-icon>mdi-dots-vertical</v-icon>
                </v-btn>
              </template>
              <v-list dense>
                <v-list-item @click="seedDemo" :disabled="isSeeding">
                  <v-list-item-icon><v-icon small>mdi-database-plus</v-icon></v-list-item-icon>
                  <v-list-item-title>Seed Demo Data (90 days)</v-list-item-title>
                </v-list-item>
                <v-list-item @click="clearDemo" :disabled="isClearing">
                  <v-list-item-icon><v-icon small>mdi-database-remove</v-icon></v-list-item-icon>
                  <v-list-item-title>Clear Demo Data</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </div>
        </div>
      </v-col>
    </v-row>

    <v-alert v-if="hasError" type="error" dismissible class="mb-4">
      {{ errorMessage }}
    </v-alert>

    <v-progress-linear v-if="isLoading" indeterminate color="#007AFF" class="mb-4" />

    <v-tabs v-model="activeTab" background-color="transparent" color="primary" class="mb-4">
      <v-tab>Platform Overview</v-tab>
      <v-tab>Talent Movement</v-tab>
      <v-tab>Engagement Health</v-tab>
    </v-tabs>

    <v-tabs-items v-model="activeTab">
      <!-- TAB 1: Platform Overview -->
      <v-tab-item>
        <v-row>
          <v-col cols="12" sm="6" md="3" v-for="stat in overviewStats" :key="stat.label">
            <v-card outlined class="pa-4 text-center">
              <div class="text-h4 font-weight-bold" :style="{ color: stat.color }">
                {{ stat.value }}
              </div>
              <div class="text-caption grey--text text--darken-1 mt-1">
                {{ stat.label }}
              </div>
              <div v-if="stat.subtitle" class="text-caption grey--text mt-1">
                {{ stat.subtitle }}
              </div>
            </v-card>
          </v-col>
        </v-row>

        <v-row class="mt-4">
          <v-col cols="12" md="6">
            <v-card outlined class="pa-4">
              <h3 class="text-subtitle-1 font-weight-bold mb-3 theme-heading">
                Movement Breakdown
              </h3>
              <div v-if="movementSummary">
                <div class="d-flex align-center mb-2" v-for="item in movementBreakdown" :key="item.label">
                  <v-icon small :color="item.color" class="mr-2">{{ item.icon }}</v-icon>
                  <span class="text-body-2 flex-grow-1">{{ item.label }}</span>
                  <span class="text-body-2 font-weight-bold">{{ item.value }}</span>
                  <span class="text-caption grey--text ml-2">({{ item.pct }}%)</span>
                </div>
              </div>
              <div v-else class="text-center py-4 grey--text">
                No movement data available
              </div>
            </v-card>
          </v-col>
          <v-col cols="12" md="6">
            <v-card outlined class="pa-4">
              <h3 class="text-subtitle-1 font-weight-bold mb-3 theme-heading">
                Search-to-Outcome Correlation
              </h3>
              <div v-if="movementSummary" class="text-center">
                <div class="text-h3 font-weight-bold" style="color: #F59E0B">
                  {{ movementSummary.correlationPct }}%
                </div>
                <div class="text-caption grey--text mt-1">
                  of movements were preceded by a search within 90 days
                </div>
                <div v-if="movementSummary.avgTimeToMovementDays" class="mt-3">
                  <div class="text-h5 font-weight-bold" style="color: #007AFF">
                    {{ movementSummary.avgTimeToMovementDays }} days
                  </div>
                  <div class="text-caption grey--text">
                    average search-to-movement time
                  </div>
                </div>
                <v-divider class="my-3" />
                <div class="text-caption grey--text text--darken-1 font-italic">
                  Correlational metric only — does not imply causation.
                  EEOC-safe: no demographic data used.
                </div>
              </div>
              <div v-else class="text-center py-4 grey--text">
                No correlation data available
              </div>
            </v-card>
          </v-col>
        </v-row>
      </v-tab-item>

      <!-- TAB 2: Talent Movement -->
      <v-tab-item>
        <v-row>
          <v-col cols="12">
            <v-card outlined class="pa-4">
              <h3 class="text-subtitle-1 font-weight-bold mb-3 theme-heading">
                Movement Timeline
              </h3>
              <div v-if="timeSeries.length > 0" class="chart-container">
                <canvas ref="timelineChart" height="250"></canvas>
              </div>
              <div v-else class="text-center py-8 grey--text">
                No timeline data available. Seed demo data to see the chart.
              </div>
            </v-card>
          </v-col>
        </v-row>

        <v-row class="mt-4">
          <v-col cols="12" md="6">
            <v-card outlined class="pa-4">
              <h3 class="text-subtitle-1 font-weight-bold mb-3 theme-heading">
                Promotions by Level
              </h3>
              <div v-if="promotionsByLevel.length > 0">
                <div v-for="level in promotionsByLevel" :key="level.level" class="mb-2">
                  <div class="d-flex align-center justify-space-between mb-1">
                    <span class="text-body-2">{{ level.level }}</span>
                    <span class="text-body-2 font-weight-bold">{{ level.count }}</span>
                  </div>
                  <v-progress-linear
                    :value="(level.count / maxPromotionCount) * 100"
                    color="#007AFF"
                    height="8"
                    rounded
                  />
                </div>
              </div>
              <div v-else class="text-center py-4 grey--text">
                No promotion data available
              </div>
            </v-card>
          </v-col>
          <v-col cols="12" md="6">
            <v-card outlined class="pa-4">
              <h3 class="text-subtitle-1 font-weight-bold mb-3 theme-heading">
                Recent Movements
              </h3>
              <v-list dense v-if="recentMovements.length > 0" class="pa-0" style="max-height: 400px; overflow-y: auto">
                <v-list-item v-for="(movement, idx) in recentMovements.slice(0, 10)" :key="idx" class="px-0">
                  <v-list-item-icon class="mr-2 my-auto">
                    <v-icon small :color="getEventColor(movement.eventType)">
                      {{ getEventIcon(movement.eventType) }}
                    </v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title class="text-body-2">
                      <strong>{{ movement.name || movement.profileId }}</strong>
                    </v-list-item-title>
                    <v-list-item-subtitle class="text-caption">
                      {{ formatMovementDescription(movement) }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                  <v-list-item-action class="my-auto">
                    <div class="d-flex align-center">
                      <v-chip v-if="movement.searchLinked" x-small color="#F59E0B" text-color="white" class="mr-1">
                        <v-icon x-small left>mdi-magnify</v-icon>
                        {{ movement.correlationDays }}d
                      </v-chip>
                      <span class="text-caption grey--text">{{ movement.date }}</span>
                    </div>
                  </v-list-item-action>
                </v-list-item>
              </v-list>
              <div v-else class="text-center py-4 grey--text">
                No recent movements
              </div>
            </v-card>
          </v-col>
        </v-row>
      </v-tab-item>

      <!-- TAB 3: Engagement Health -->
      <v-tab-item>
        <div class="text-caption grey--text mb-3" style="font-style: italic;">
          Engagement metrics reflect all-time profile completeness across the organization, independent of the selected time period.
        </div>
        <v-row>
          <v-col cols="12" sm="6" md="3" v-for="metric in engagementMetrics" :key="metric.label">
            <v-card outlined class="pa-4 text-center">
              <div class="text-h4 font-weight-bold" :style="{ color: metric.color }">
                {{ metric.value }}
              </div>
              <div class="text-caption grey--text text--darken-1 mt-1">
                {{ metric.label }}
              </div>
            </v-card>
          </v-col>
        </v-row>

        <v-row class="mt-4">
          <v-col cols="12" md="6">
            <v-card outlined class="pa-4">
              <h3 class="text-subtitle-1 font-weight-bold mb-3 theme-heading">
                Achievements per Profile
              </h3>
              <div class="text-center mb-3">
                <div class="text-h3 font-weight-bold" :style="{ color: achievementHealthColor }">
                  {{ achievementsPerProfile }}
                </div>
                <div class="text-caption grey--text mt-1">current ratio</div>
              </div>
              <v-progress-linear
                :value="achievementHealthPct"
                :color="achievementHealthColor"
                height="12"
                rounded
                class="mb-2"
              />
              <div class="d-flex justify-space-between text-caption grey--text">
                <span>Needs Attention (&lt;2)</span>
                <span>Healthy (3-5)</span>
                <span>Strong (&gt;5)</span>
              </div>
              <v-divider class="my-3" />
              <div class="text-caption">
                <strong>Benchmark:</strong> Organizations with 3+ achievements per profile
                show 40% higher internal mobility rates.
              </div>
            </v-card>
          </v-col>
          <v-col cols="12" md="6">
            <v-card outlined class="pa-4">
              <h3 class="text-subtitle-1 font-weight-bold mb-3 theme-heading">
                Wins per Profile
              </h3>
              <div class="text-center mb-3">
                <div class="text-h3 font-weight-bold" :style="{ color: winsHealthColor }">
                  {{ winsPerProfile }}
                </div>
                <div class="text-caption grey--text mt-1">current ratio</div>
              </div>
              <v-progress-linear
                :value="winsHealthPct"
                :color="winsHealthColor"
                height="12"
                rounded
                class="mb-2"
              />
              <div class="d-flex justify-space-between text-caption grey--text">
                <span>Low (&lt;0.3)</span>
                <span>Growing (0.3-1.0)</span>
                <span>Active (&gt;1.0)</span>
              </div>
              <v-divider class="my-3" />
              <div class="text-caption">
                <strong>Benchmark:</strong> Regular wins documentation correlates with
                2x higher profile completeness and better career outcomes.
              </div>
            </v-card>
          </v-col>
        </v-row>

        <v-row class="mt-4">
          <v-col cols="12">
            <v-card outlined class="pa-4">
              <h3 class="text-subtitle-1 font-weight-bold mb-3 theme-heading">
                Engagement Health Summary
              </h3>
              <v-simple-table dense>
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th>Metric</th>
                      <th>Current</th>
                      <th>Benchmark</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="row in healthSummaryRows" :key="row.metric">
                      <td>{{ row.metric }}</td>
                      <td class="font-weight-bold">{{ row.current }}</td>
                      <td class="grey--text">{{ row.benchmark }}</td>
                      <td>
                        <v-chip x-small :color="row.statusColor" text-color="white">
                          {{ row.status }}
                        </v-chip>
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </v-card>
          </v-col>
        </v-row>
      </v-tab-item>
    </v-tabs-items>

    <v-snackbar v-model="snackbar" :timeout="3000" :color="snackbarColor">
      {{ snackbarText }}
    </v-snackbar>
  </component>
</template>

<script>
import { mapState, mapGetters, mapActions, mapMutations } from "vuex";

export default {
  name: "MovementAnalytics",
  props: {
    embedded: { type: Boolean, default: false },
  },
  data() {
    return {
      selectedPeriodIdx: 2,
      periodOptions: [7, 14, 30, 90],
      snackbar: false,
      snackbarText: "",
      snackbarColor: "success",
      chartInstance: null,
    };
  },
  computed: {
    ...mapState("movementAnalytics", [
      "analytics",
      "periodDays",
      "isLoading",
      "hasError",
      "errorMessage",
      "isSeeding",
      "isClearing",
    ]),
    ...mapGetters("movementAnalytics", [
      "engagement",
      "movementSummary",
      "timeSeries",
      "recentMovements",
      "promotionsByLevel",
      "hasData",
    ]),
    activeTab: {
      get() {
        return this.$store.state.movementAnalytics.activeTab;
      },
      set(val) {
        this.$store.commit("movementAnalytics/setActiveTab", val);
      },
    },
    overviewStats() {
      if (!this.movementSummary || !this.engagement) return [];
      return [
        {
          label: "Total Movements",
          value: this.movementSummary.totalMovements,
          color: this.$vuetify.theme.dark ? "#90CAF9" : "#1E3A5F",
          subtitle: `in last ${this.periodDays} days`,
        },
        {
          label: "Promotions",
          value: this.movementSummary.promotions,
          color: "#10B981",
        },
        {
          label: "Job Changes",
          value: this.movementSummary.jobChanges,
          color: "#007AFF",
        },
        {
          label: "Lateral Moves",
          value: this.movementSummary.lateralMoves,
          color: "#8B5CF6",
        },
      ];
    },
    movementBreakdown() {
      if (!this.movementSummary) return [];
      const total = this.movementSummary.totalMovements || 1;
      return [
        {
          label: "Job Changes",
          value: this.movementSummary.jobChanges,
          pct: Math.round((this.movementSummary.jobChanges / total) * 100),
          icon: "mdi-briefcase-arrow-left-right",
          color: "#007AFF",
        },
        {
          label: "Promotions",
          value: this.movementSummary.promotions,
          pct: Math.round((this.movementSummary.promotions / total) * 100),
          icon: "mdi-arrow-up-bold",
          color: "#10B981",
        },
        {
          label: "Lateral Moves",
          value: this.movementSummary.lateralMoves,
          pct: Math.round((this.movementSummary.lateralMoves / total) * 100),
          icon: "mdi-arrow-left-right",
          color: "#8B5CF6",
        },
        {
          label: "Search-Correlated",
          value: this.movementSummary.searchCorrelated,
          pct: Math.round(
            (this.movementSummary.searchCorrelated / total) * 100
          ),
          icon: "mdi-magnify",
          color: "#F59E0B",
        },
      ];
    },
    maxPromotionCount() {
      if (!this.promotionsByLevel.length) return 1;
      return Math.max(...this.promotionsByLevel.map((l) => l.count), 1);
    },
    engagementMetrics() {
      if (!this.engagement) return [];
      return [
        {
          label: "Total Profiles",
          value: this.engagement.totalProfiles,
          color: this.$vuetify.theme.dark ? "#90CAF9" : "#1E3A5F",
        },
        {
          label: "Total Achievements",
          value: this.engagement.totalAchievements,
          color: "#007AFF",
        },
        {
          label: "Total Wins",
          value: this.engagement.totalWins,
          color: "#10B981",
        },
        {
          label: "Achievements/Profile",
          value: this.engagement.achievementsPerProfile,
          color: this.achievementHealthColor,
        },
      ];
    },
    achievementsPerProfile() {
      return this.engagement?.achievementsPerProfile ?? 0;
    },
    winsPerProfile() {
      return this.engagement?.winsPerProfile ?? 0;
    },
    achievementHealthColor() {
      const v = this.achievementsPerProfile;
      if (v >= 5) return "#10B981";
      if (v >= 3) return "#007AFF";
      if (v >= 2) return "#F59E0B";
      return "#EF4444";
    },
    achievementHealthPct() {
      return Math.min((this.achievementsPerProfile / 7) * 100, 100);
    },
    winsHealthColor() {
      const v = this.winsPerProfile;
      if (v >= 1.0) return "#10B981";
      if (v >= 0.3) return "#007AFF";
      return "#F59E0B";
    },
    winsHealthPct() {
      return Math.min((this.winsPerProfile / 2) * 100, 100);
    },
    healthSummaryRows() {
      if (!this.engagement || !this.movementSummary) return [];
      return [
        {
          metric: "Achievements per Profile",
          current: this.achievementsPerProfile,
          benchmark: "3.0 - 5.0",
          status: this.achievementsPerProfile >= 3 ? "Healthy" : "Needs Work",
          statusColor: this.achievementsPerProfile >= 3 ? "#10B981" : "#F59E0B",
        },
        {
          metric: "Wins per Profile",
          current: this.winsPerProfile,
          benchmark: "0.5 - 1.0",
          status: this.winsPerProfile >= 0.5 ? "Healthy" : "Needs Work",
          statusColor: this.winsPerProfile >= 0.5 ? "#10B981" : "#F59E0B",
        },
        {
          metric: "Search-Movement Correlation",
          current: this.movementSummary.correlationPct + "%",
          benchmark: "30% - 50%",
          status:
            this.movementSummary.correlationPct >= 30 ? "Active" : "Building",
          statusColor:
            this.movementSummary.correlationPct >= 30 ? "#007AFF" : "#F59E0B",
        },
        {
          metric: "Total Profiles",
          current: this.engagement.totalProfiles,
          benchmark: "Varies",
          status: this.engagement.totalProfiles > 0 ? "Active" : "Empty",
          statusColor:
            this.engagement.totalProfiles > 0 ? "#10B981" : "#EF4444",
        },
      ];
    },
  },
  watch: {
    selectedPeriodIdx(newIdx) {
      const days = this.periodOptions[newIdx];
      this.changePeriod(days);
    },
    timeSeries() {
      if (this.activeTab === 1) {
        this.$nextTick(() => {
          this.renderChart();
        });
      }
    },
    activeTab(val) {
      if (val === 1) {
        this.$nextTick(() => {
          setTimeout(() => {
            this.renderChart();
          }, 100);
        });
      }
    },
  },
  methods: {
    ...mapActions("movementAnalytics", [
      "loadAnalytics",
      "seedDemoData",
      "clearDemoData",
      "changePeriod",
    ]),
    ...mapMutations("movementAnalytics", ["setActiveTab"]),
    async seedDemo() {
      const success = await this.seedDemoData(90);
      if (success) {
        this.showSnackbar("Demo data seeded successfully", "success");
      } else {
        this.showSnackbar("Failed to seed demo data", "error");
      }
    },
    async clearDemo() {
      const success = await this.clearDemoData();
      if (success) {
        this.showSnackbar("Demo data cleared", "success");
      } else {
        this.showSnackbar("Failed to clear demo data", "error");
      }
    },
    showSnackbar(text, color = "success") {
      this.snackbarText = text;
      this.snackbarColor = color;
      this.snackbar = true;
    },
    getEventIcon(type) {
      const icons = {
        job_change: "mdi-briefcase-arrow-left-right",
        promotion: "mdi-arrow-up-bold",
        lateral_move: "mdi-arrow-left-right",
      };
      return icons[type] || "mdi-help-circle";
    },
    getEventColor(type) {
      const colors = {
        job_change: "#007AFF",
        promotion: "#10B981",
        lateral_move: "#8B5CF6",
      };
      return colors[type] || "#9E9E9E";
    },
    formatMovementDescription(m) {
      switch (m.eventType) {
        case "job_change":
          return `${m.fromCompany} → ${m.toCompany}`;
        case "promotion":
          return `${m.fromSeniority} → ${m.toSeniority}`;
        case "lateral_move":
          return `${m.fromTitle} → ${m.toTitle}`;
        default:
          return `${m.fromTitle} → ${m.toTitle}`;
      }
    },
    loadChartJs() {
      return new Promise((resolve, reject) => {
        if (typeof window.Chart !== 'undefined') {
          resolve(window.Chart);
          return;
        }
        const existing = document.querySelector('script[src*="chart.js"]') || document.querySelector('script[src*="Chart"]');
        if (existing) {
          existing.addEventListener('load', () => resolve(window.Chart));
          return;
        }
        const script = document.createElement('script');
        script.src = 'https://cdn.jsdelivr.net/npm/chart.js@4.4.1/dist/chart.umd.min.js';
        script.onload = () => resolve(window.Chart);
        script.onerror = () => reject(new Error('Failed to load Chart.js'));
        document.head.appendChild(script);
      });
    },
    async renderChart() {
      if (!this.$refs.timelineChart || !this.timeSeries.length) return;

      if (this.chartInstance) {
        this.chartInstance.destroy();
        this.chartInstance = null;
      }

      try {
        await this.loadChartJs();
      } catch (e) {
        return;
      }

      const labels = this.timeSeries
        .filter((_, i) => i % Math.max(1, Math.floor(this.timeSeries.length / 15)) === 0)
        .map((entry) => entry.date.substring(5));

      const sampledData = this.timeSeries.filter(
        (_, i) => i % Math.max(1, Math.floor(this.timeSeries.length / 15)) === 0
      );

      const ctx = this.$refs.timelineChart.getContext("2d");
      this.chartInstance = new window.Chart(ctx, {
        type: "line",
        data: {
          labels: labels,
          datasets: [
            {
              label: "Cumulative Changes",
              data: sampledData.map((d) => d.cumulativeChanges),
              borderColor: "#007AFF",
              backgroundColor: "rgba(0, 122, 255, 0.1)",
              fill: true,
              tension: 0.3,
              pointRadius: 2,
            },
            {
              label: "Cumulative Promotions",
              data: sampledData.map((d) => d.cumulativePromotions),
              borderColor: "#10B981",
              backgroundColor: "rgba(16, 185, 129, 0.1)",
              fill: true,
              tension: 0.3,
              pointRadius: 2,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              position: "top",
              labels: { usePointStyle: true, padding: 15 },
            },
          },
          scales: {
            x: {
              grid: { display: false },
              ticks: { maxRotation: 45 },
            },
            y: {
              beginAtZero: true,
              ticks: { stepSize: 1 },
            },
          },
        },
      });
    },
  },
  async mounted() {
    await this.loadAnalytics(this.periodOptions[this.selectedPeriodIdx]);
  },
  beforeDestroy() {
    if (this.chartInstance) {
      this.chartInstance.destroy();
      this.chartInstance = null;
    }
  },
};
</script>

<style scoped>
.movement-analytics {
  max-width: 1400px;
}

.chart-container {
  position: relative;
  height: 250px;
  width: 100%;
}
</style>
